// App lifecycle dialogs: scoped reset, restore points, version restore (DES1 `reset-confirm`,
// `reset-done`, Data tab, Versions tab).
//
// Namespace `app-lifecycle`; owner: T2-UI1 (ui/workspace/apps/lifecycle). "Restore a version"
// restores the app's code only; "restore a point" brings entries and photos back: they are
// different and say so. `{app}` the app name, `{count}` a number, `{people}` a count, `{team}` the
// audience name, `{days}` retention, `{since}` a formatted date, `{system}` a connection name.
// Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  close: 'Close',
  cancel: 'Cancel',
  resetTitle: 'Clear all entries in {app}?',
  resetLead:
    'Only this app’s entries and photos are cleared. Other apps, {system} and company data are not touched.',
  resetAudience: '{team} ({people} people) uses this app. They’ll see an empty list right away.',
  resetCleared: '{entries} entries and {photos} photos are cleared.',
  resetRestorePoint: 'A restore point is saved first. You can restore it for {days} days.',
  resetKeptStays: 'Kept history stays: weekly counts since {since} are not cleared.',
  resetExternalStays: 'Claim notes already added in {system} stay there.',
  resetTypeLabel: 'Type {app} to confirm',
  resetOwnerOnly: 'Only you, as owner, can do this, and only here. Chat can’t clear live data.',
  resetNotAllowed: 'Only the owner can clear entries. {owner} can do this from this tab.',
  resetConfirm: 'Clear {entries} entries',
  resetBusy: 'Clearing…',
  resetDoneTitle: 'Entries cleared',
  resetDoneBody:
    '{entries} entries and {photos} photos were cleared. Restore the saved point from Data for {days} days.',
  restorePointsHeading: 'Restore points',
  restorePointsKept: 'Kept {days} days',
  restorePointsNote:
    'Restoring brings back entries and photos as they were. Entries added since are kept aside, not lost.',
  restorePointsEmpty: 'No restore points yet. One is saved before anything is cleared or restored.',
  restorePointRow: '{label}',
  restorePointCounts: '{entries} entries and {photos} photos',
  restore: 'Restore…',
  restorePointTitle: 'Restore {label}?',
  restorePointLead: 'Pick the restore point. Nothing is selected until you choose.',
  restorePointKeptAside: '{count} entries added since are kept aside, not lost.',
  restorePointSaveFirst: 'A restore point of today is saved first.',
  restorePointConfirm: 'Restore this point',
  restorePointBusy: 'Restoring…',
  restorePointNoneChosen: 'Choose a restore point to continue.',
  versionRestoreTitle: 'Restore v{version}?',
  versionRestoreLead:
    'This brings back how the app looks and works in v{version}. It does not change any entries or photos: those stay as they are.',
  versionRestoreDraft: 'It becomes a new draft. {team} keeps using v{published} until you publish.',
  versionRestoreConfirm: 'Restore v{version} as a draft',
  versionRestoreBusy: 'Restoring…',
  errorGeneric: 'That didn’t work. Nothing was changed. Try again.',
  resetUnknown: 'We couldn’t confirm whether the reset finished.',
  restorePointUnknown: 'We couldn’t confirm whether the restore finished.',
  restoreVersionUnknown: 'We couldn’t confirm whether version {version} was restored.',
  unknownBlockedHint: 'You can try again once we know what happened.',
  unknownNoCheckHint: 'Reload the page to see the current state.',
  checkingOutcome: 'Checking…',
};

export type AppLifecycleCopyKey = keyof typeof en;
type Copy = Record<AppLifecycleCopyKey, string>;

const sv: Copy = {
  close: 'Stäng',
  cancel: 'Avbryt',
  resetTitle: 'Rensa alla poster i {app}?',
  resetLead:
    'Bara den här appens poster och foton rensas. Andra appar, {system} och företagsdata berörs inte.',
  resetAudience: '{team} ({people} personer) använder appen. De ser en tom lista direkt.',
  resetCleared: '{entries} poster och {photos} foton rensas.',
  resetRestorePoint: 'En återställningspunkt sparas först. Du kan återställa den i {days} dagar.',
  resetKeptStays: 'Sparad historik finns kvar: veckosummor sedan {since} rensas inte.',
  resetExternalStays: 'Anspråksnoter som redan lagts i {system} finns kvar där.',
  resetTypeLabel: 'Skriv {app} för att bekräfta',
  resetOwnerOnly:
    'Bara du, som ägare, kan göra detta, och bara här. Chatten kan inte rensa live-data.',
  resetNotAllowed: 'Bara ägaren kan rensa poster. {owner} kan göra det från den här fliken.',
  resetConfirm: 'Rensa {entries} poster',
  resetBusy: 'Rensar…',
  resetDoneTitle: 'Posterna är rensade',
  resetDoneBody:
    '{entries} poster och {photos} foton rensades. Återställ den sparade punkten under Data i {days} dagar.',
  restorePointsHeading: 'Återställningspunkter',
  restorePointsKept: 'Sparas {days} dagar',
  restorePointsNote:
    'Återställning ger tillbaka poster och foton som de var. Poster som lagts till sedan läggs åt sidan, de går inte förlorade.',
  restorePointsEmpty:
    'Inga återställningspunkter än. En sparas innan något rensas eller återställs.',
  restorePointRow: '{label}',
  restorePointCounts: '{entries} poster och {photos} foton',
  restore: 'Återställ…',
  restorePointTitle: 'Återställa {label}?',
  restorePointLead: 'Välj återställningspunkt. Inget är valt förrän du väljer.',
  restorePointKeptAside:
    '{count} poster som lagts till sedan läggs åt sidan, de går inte förlorade.',
  restorePointSaveFirst: 'En återställningspunkt av idag sparas först.',
  restorePointConfirm: 'Återställ den här punkten',
  restorePointBusy: 'Återställer…',
  restorePointNoneChosen: 'Välj en återställningspunkt för att fortsätta.',
  versionRestoreTitle: 'Återställa v{version}?',
  versionRestoreLead:
    'Det här ger tillbaka hur appen ser ut och fungerar i v{version}. Inga poster eller foton ändras: de är som de är.',
  versionRestoreDraft:
    'Det blir ett nytt utkast. {team} fortsätter använda v{published} tills du publicerar.',
  versionRestoreConfirm: 'Återställ v{version} som utkast',
  versionRestoreBusy: 'Återställer…',
  errorGeneric: 'Det gick inte. Inget ändrades. Försök igen.',
  resetUnknown: 'Vi kunde inte bekräfta om rensningen blev klar.',
  restorePointUnknown: 'Vi kunde inte bekräfta om återställningen blev klar.',
  restoreVersionUnknown: 'Vi kunde inte bekräfta om version {version} återställdes.',
  unknownBlockedHint: 'Du kan försöka igen när vi vet vad som hände.',
  unknownNoCheckHint: 'Ladda om sidan för att se hur det ser ut nu.',
  checkingOutcome: 'Kontrollerar…',
};

// Full translations (DES2); keys and placeholders identical to `en`.
const translated: Record<string, Copy> = {
  bg: {
    close: 'Затвори',
    cancel: 'Отказ',
    resetTitle: 'Да се изчистят ли всички записи в {app}?',
    resetLead:
      'Изчистват се само записите и снимките на това приложение. Другите приложения, {system} и данните на компанията не се засягат.',
    resetAudience:
      '{team} ({people} души) използва това приложение. Те веднага ще видят празен списък.',
    resetCleared: 'Изчистват се {entries} записа и {photos} снимки.',
    resetRestorePoint:
      'Първо се запазва точка за възстановяване. Можете да я възстановите в рамките на {days} дни.',
    resetKeptStays:
      'Запазената история остава: седмичните бройки от {since} насам не се изчистват.',
    resetExternalStays: 'Бележките по претенции, вече добавени в {system}, остават там.',
    resetTypeLabel: 'Въведете {app}, за да потвърдите',
    resetOwnerOnly:
      'Само Вие като собственик можете да направите това, и то само тук. Чатът не може да изчиства реални данни.',
    resetNotAllowed:
      'Само собственикът може да изчиства записи. {owner} може да го направи от този раздел.',
    resetConfirm: 'Изчистване на {entries} записа',
    resetBusy: 'Изчистване…',
    resetDoneTitle: 'Записите са изчистени',
    resetDoneBody:
      '{entries} записа и {photos} снимки бяха изчистени. Възстановете запазената точка от „Данни“ в рамките на {days} дни.',
    restorePointsHeading: 'Точки за възстановяване',
    restorePointsKept: 'Пазят се {days} дни',
    restorePointsNote:
      'Възстановяването връща записите и снимките такива, каквито са били. Записите, добавени след това, се отделят настрана и не се губят.',
    restorePointsEmpty:
      'Все още няма точки за възстановяване. Такава се запазва, преди нещо да бъде изчистено или възстановено.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} записа и {photos} снимки',
    restore: 'Възстановяване…',
    restorePointTitle: 'Да се възстанови ли {label}?',
    restorePointLead: 'Изберете точката за възстановяване. Нищо не е избрано, докато не изберете.',
    restorePointKeptAside: '{count} записа, добавени след това, се отделят настрана и не се губят.',
    restorePointSaveFirst: 'Първо се запазва точка за възстановяване към днешна дата.',
    restorePointConfirm: 'Възстановяване на тази точка',
    restorePointBusy: 'Възстановяване…',
    restorePointNoneChosen: 'Изберете точка за възстановяване, за да продължите.',
    versionRestoreTitle: 'Да се възстанови ли v{version}?',
    versionRestoreLead:
      'Това връща вида и работата на приложението от v{version}. Не променя никакви записи или снимки: те остават каквито са.',
    versionRestoreDraft:
      'Тя става нова чернова. {team} продължава да използва v{published}, докато не публикувате.',
    versionRestoreConfirm: 'Възстановяване на v{version} като чернова',
    versionRestoreBusy: 'Възстановяване…',
    errorGeneric: 'Не се получи. Нищо не е променено. Опитайте отново.',
    resetUnknown: 'Не успяхме да потвърдим дали изчистването е завършило.',
    restorePointUnknown: 'Не успяхме да потвърдим дали възстановяването е завършило.',
    restoreVersionUnknown: 'Не успяхме да потвърдим дали версия {version} е възстановена.',
    unknownBlockedHint: 'Можете да опитате отново, след като разберем какво се е случило.',
    unknownNoCheckHint: 'Презаредете страницата, за да видите текущото състояние.',
    checkingOutcome: 'Проверка…',
  },
  cs: {
    close: 'Zavřít',
    cancel: 'Zrušit',
    resetTitle: 'Vymazat všechny záznamy v {app}?',
    resetLead:
      'Vymažou se jen záznamy a fotky této aplikace. Ostatních aplikací, {system} ani firemních dat se to nedotkne.',
    resetAudience:
      'Tuto aplikaci používá {team} (počet lidí: {people}). Prázdný seznam uvidí hned.',
    resetCleared: 'Vymaže se – záznamy: {entries}, fotky: {photos}.',
    resetRestorePoint:
      'Nejprve se uloží bod obnovení. Obnovit ho můžete po omezenou dobu (počet dní: {days}).',
    resetKeptStays: 'Uchovaná historie zůstává: týdenní souhrny od {since} se nevymažou.',
    resetExternalStays: 'Poznámky k nárokům, které už jsou v {system}, tam zůstanou.',
    resetTypeLabel: 'Pro potvrzení napište {app}',
    resetOwnerOnly:
      'Udělat to můžete jen vy jako vlastník, a jen tady. Chat nemůže vymazat živá data.',
    resetNotAllowed: 'Záznamy může vymazat jen vlastník. {owner} to může udělat na této kartě.',
    resetConfirm: 'Vymazat záznamy: {entries}',
    resetBusy: 'Mazání…',
    resetDoneTitle: 'Záznamy vymazány',
    resetDoneBody:
      'Vymazáno – záznamy: {entries}, fotky: {photos}. Uložený bod můžete obnovit v části Data po omezenou dobu (počet dní: {days}).',
    restorePointsHeading: 'Body obnovení',
    restorePointsKept: 'Uchování (počet dní): {days}',
    restorePointsNote:
      'Obnovení vrátí záznamy a fotky do původního stavu. Záznamy přidané mezitím se odloží stranou, neztratí se.',
    restorePointsEmpty:
      'Zatím žádné body obnovení. Jeden se uloží před každým vymazáním nebo obnovením.',
    restorePointRow: '{label}',
    restorePointCounts: 'Záznamy: {entries}, fotky: {photos}',
    restore: 'Obnovit…',
    restorePointTitle: 'Obnovit {label}?',
    restorePointLead: 'Vyberte bod obnovení. Dokud nevyberete, nic není vybráno.',
    restorePointKeptAside:
      'Záznamy přidané mezitím (počet: {count}) se odloží stranou, neztratí se.',
    restorePointSaveFirst: 'Nejprve se uloží dnešní bod obnovení.',
    restorePointConfirm: 'Obnovit tento bod',
    restorePointBusy: 'Obnovování…',
    restorePointNoneChosen: 'Pokračujte výběrem bodu obnovení.',
    versionRestoreTitle: 'Obnovit v{version}?',
    versionRestoreLead:
      'Vrátí se vzhled a fungování aplikace z v{version}. Nezmění se žádné záznamy ani fotky: zůstanou, jak jsou.',
    versionRestoreDraft:
      'Vznikne nový koncept. {team} bude dál používat v{published}, dokud ho nepublikujete.',
    versionRestoreConfirm: 'Obnovit v{version} jako koncept',
    versionRestoreBusy: 'Obnovování…',
    errorGeneric: 'Nepovedlo se. Nic se nezměnilo. Zkuste to znovu.',
    resetUnknown: 'Nepodařilo se ověřit, zda se vymazání dokončilo.',
    restorePointUnknown: 'Nepodařilo se ověřit, zda se obnovení dokončilo.',
    restoreVersionUnknown: 'Nepodařilo se ověřit, zda byla verze {version} obnovena.',
    unknownBlockedHint: 'Zkusit to znovu můžete, až budeme vědět, co se stalo.',
    unknownNoCheckHint: 'Načtěte stránku znovu, abyste viděli aktuální stav.',
    checkingOutcome: 'Kontrola…',
  },
  da: {
    close: 'Luk',
    cancel: 'Annuller',
    resetTitle: 'Ryd alle poster i {app}?',
    resetLead:
      'Kun denne apps poster og fotos ryddes. Andre apps, {system} og virksomhedens data berøres ikke.',
    resetAudience:
      '{team} ({people} personer) bruger denne app. De ser en tom liste med det samme.',
    resetCleared: '{entries} poster og {photos} fotos ryddes.',
    resetRestorePoint: 'Der gemmes først et gendannelsespunkt. Du kan gendanne det i {days} dage.',
    resetKeptStays: 'Gemt historik bevares: ugentlige optællinger siden {since} ryddes ikke.',
    resetExternalStays: 'Sagsnoter, der allerede er tilføjet i {system}, bliver der.',
    resetTypeLabel: 'Skriv {app} for at bekræfte',
    resetOwnerOnly: 'Kun du som ejer kan gøre dette, og kun her. Chatten kan ikke rydde livedata.',
    resetNotAllowed: 'Kun ejeren kan rydde poster. {owner} kan gøre det fra denne fane.',
    resetConfirm: 'Ryd {entries} poster',
    resetBusy: 'Rydder…',
    resetDoneTitle: 'Posterne er ryddet',
    resetDoneBody:
      '{entries} poster og {photos} fotos blev ryddet. Gendan det gemte punkt under Data i {days} dage.',
    restorePointsHeading: 'Gendannelsespunkter',
    restorePointsKept: 'Gemmes i {days} dage',
    restorePointsNote:
      'Gendannelse bringer poster og fotos tilbage, som de var. Poster, der er tilføjet siden, lægges til side og går ikke tabt.',
    restorePointsEmpty:
      'Ingen gendannelsespunkter endnu. Der gemmes et, før noget ryddes eller gendannes.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} poster og {photos} fotos',
    restore: 'Gendan…',
    restorePointTitle: 'Gendan {label}?',
    restorePointLead: 'Vælg gendannelsespunktet. Intet er valgt, før du vælger.',
    restorePointKeptAside:
      '{count} poster, der er tilføjet siden, lægges til side og går ikke tabt.',
    restorePointSaveFirst: 'Der gemmes først et gendannelsespunkt for i dag.',
    restorePointConfirm: 'Gendan dette punkt',
    restorePointBusy: 'Gendanner…',
    restorePointNoneChosen: 'Vælg et gendannelsespunkt for at fortsætte.',
    versionRestoreTitle: 'Gendan v{version}?',
    versionRestoreLead:
      'Det bringer appens udseende og funktion fra v{version} tilbage. Ingen poster eller fotos ændres: de forbliver, som de er.',
    versionRestoreDraft:
      'Den bliver en ny kladde. {team} bruger fortsat v{published}, indtil du udgiver.',
    versionRestoreConfirm: 'Gendan v{version} som kladde',
    versionRestoreBusy: 'Gendanner…',
    errorGeneric: 'Det virkede ikke. Intet blev ændret. Prøv igen.',
    resetUnknown: 'Vi kunne ikke bekræfte, om rydningen blev fuldført.',
    restorePointUnknown: 'Vi kunne ikke bekræfte, om gendannelsen blev fuldført.',
    restoreVersionUnknown: 'Vi kunne ikke bekræfte, om version {version} blev gendannet.',
    unknownBlockedHint: 'Du kan prøve igen, når vi ved, hvad der skete.',
    unknownNoCheckHint: 'Genindlæs siden for at se den aktuelle tilstand.',
    checkingOutcome: 'Tjekker…',
  },
  de: {
    close: 'Schließen',
    cancel: 'Abbrechen',
    resetTitle: 'Alle Einträge in {app} leeren?',
    resetLead:
      'Nur die Einträge und Fotos dieser App werden geleert. Andere Apps, {system} und Unternehmensdaten bleiben unberührt.',
    resetAudience:
      '{team} ({people} Personen) nutzt diese App. Alle sehen sofort eine leere Liste.',
    resetCleared: '{entries} Einträge und {photos} Fotos werden geleert.',
    resetRestorePoint:
      'Zuerst wird ein Wiederherstellungspunkt gespeichert. Sie können ihn {days} Tage lang wiederherstellen.',
    resetKeptStays: 'Aufbewahrter Verlauf bleibt: Wochenzahlen seit {since} werden nicht geleert.',
    resetExternalStays:
      'Schadensnotizen, die bereits in {system} hinzugefügt wurden, bleiben dort.',
    resetTypeLabel: 'Geben Sie zur Bestätigung {app} ein',
    resetOwnerOnly:
      'Nur Sie als Inhaber können das tun, und nur hier. Der Chat kann keine Live-Daten leeren.',
    resetNotAllowed: 'Nur der Inhaber kann Einträge leeren. {owner} kann das in diesem Tab tun.',
    resetConfirm: '{entries} Einträge leeren',
    resetBusy: 'Wird geleert…',
    resetDoneTitle: 'Einträge geleert',
    resetDoneBody:
      '{entries} Einträge und {photos} Fotos wurden geleert. Sie können den gespeicherten Punkt {days} Tage lang unter Daten wiederherstellen.',
    restorePointsHeading: 'Wiederherstellungspunkte',
    restorePointsKept: '{days} Tage aufbewahrt',
    restorePointsNote:
      'Beim Wiederherstellen kommen Einträge und Fotos so zurück, wie sie waren. Seitdem hinzugefügte Einträge werden beiseitegelegt und gehen nicht verloren.',
    restorePointsEmpty:
      'Noch keine Wiederherstellungspunkte. Vor jedem Leeren oder Wiederherstellen wird einer gespeichert.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} Einträge und {photos} Fotos',
    restore: 'Wiederherstellen…',
    restorePointTitle: '{label} wiederherstellen?',
    restorePointLead:
      'Wählen Sie den Wiederherstellungspunkt. Bis Sie wählen, ist nichts ausgewählt.',
    restorePointKeptAside:
      '{count} seitdem hinzugefügte Einträge werden beiseitegelegt und gehen nicht verloren.',
    restorePointSaveFirst: 'Zuerst wird ein Wiederherstellungspunkt von heute gespeichert.',
    restorePointConfirm: 'Diesen Punkt wiederherstellen',
    restorePointBusy: 'Wird wiederhergestellt…',
    restorePointNoneChosen: 'Wählen Sie einen Wiederherstellungspunkt, um fortzufahren.',
    versionRestoreTitle: 'v{version} wiederherstellen?',
    versionRestoreLead:
      'Damit kehren Aussehen und Funktionsweise der App aus v{version} zurück. Einträge und Fotos ändern sich dadurch nicht, sie bleiben, wie sie sind.',
    versionRestoreDraft:
      'Daraus wird ein neuer Entwurf. {team} nutzt weiter v{published}, bis Sie veröffentlichen.',
    versionRestoreConfirm: 'v{version} als Entwurf wiederherstellen',
    versionRestoreBusy: 'Wird wiederhergestellt…',
    errorGeneric: 'Das hat nicht funktioniert. Es wurde nichts geändert. Versuchen Sie es erneut.',
    resetUnknown: 'Wir konnten nicht bestätigen, ob das Leeren abgeschlossen wurde.',
    restorePointUnknown:
      'Wir konnten nicht bestätigen, ob die Wiederherstellung abgeschlossen wurde.',
    restoreVersionUnknown:
      'Wir konnten nicht bestätigen, ob Version {version} wiederhergestellt wurde.',
    unknownBlockedHint: 'Sie können es erneut versuchen, sobald wir wissen, was passiert ist.',
    unknownNoCheckHint: 'Laden Sie die Seite neu, um den aktuellen Stand zu sehen.',
    checkingOutcome: 'Wird geprüft…',
  },
  el: {
    close: 'Κλείσιμο',
    cancel: 'Ακύρωση',
    resetTitle: 'Εκκαθάριση όλων των εγγραφών στο {app};',
    resetLead:
      'Εκκαθαρίζονται μόνο οι εγγραφές και οι φωτογραφίες αυτής της εφαρμογής. Άλλες εφαρμογές, το {system} και τα δεδομένα της εταιρείας δεν επηρεάζονται.',
    resetAudience:
      'Την εφαρμογή χρησιμοποιεί {team} ({people} άτομα). Θα δουν αμέσως μια κενή λίστα.',
    resetCleared: 'Εκκαθαρίζονται {entries} εγγραφές και {photos} φωτογραφίες.',
    resetRestorePoint:
      'Πρώτα αποθηκεύεται ένα σημείο επαναφοράς. Μπορείτε να το επαναφέρετε για {days} ημέρες.',
    resetKeptStays:
      'Το ιστορικό που διατηρείται παραμένει: οι εβδομαδιαίες μετρήσεις από {since} δεν εκκαθαρίζονται.',
    resetExternalStays:
      'Οι σημειώσεις αξιώσεων που έχουν ήδη προστεθεί στο {system} παραμένουν εκεί.',
    resetTypeLabel: 'Πληκτρολογήστε {app} για επιβεβαίωση',
    resetOwnerOnly:
      'Μόνο εσείς, ως κάτοχος, μπορείτε να το κάνετε αυτό, και μόνο εδώ. Η συνομιλία δεν μπορεί να εκκαθαρίσει ενεργά δεδομένα.',
    resetNotAllowed:
      'Μόνο ο κάτοχος μπορεί να εκκαθαρίσει εγγραφές. {owner} μπορεί να το κάνει από αυτή την καρτέλα.',
    resetConfirm: 'Εκκαθάριση {entries} εγγραφών',
    resetBusy: 'Εκκαθάριση…',
    resetDoneTitle: 'Οι εγγραφές εκκαθαρίστηκαν',
    resetDoneBody:
      'Εκκαθαρίστηκαν {entries} εγγραφές και {photos} φωτογραφίες. Επαναφέρετε το αποθηκευμένο σημείο από την καρτέλα Δεδομένα για {days} ημέρες.',
    restorePointsHeading: 'Σημεία επαναφοράς',
    restorePointsKept: 'Διατηρούνται {days} ημέρες',
    restorePointsNote:
      'Η επαναφορά φέρνει πίσω τις εγγραφές και τις φωτογραφίες όπως ήταν. Οι εγγραφές που προστέθηκαν στο μεταξύ φυλάσσονται χωριστά και δεν χάνονται.',
    restorePointsEmpty:
      'Δεν υπάρχουν ακόμη σημεία επαναφοράς. Ένα αποθηκεύεται πριν από κάθε εκκαθάριση ή επαναφορά.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} εγγραφές και {photos} φωτογραφίες',
    restore: 'Επαναφορά…',
    restorePointTitle: 'Επαναφορά του {label};',
    restorePointLead:
      'Επιλέξτε το σημείο επαναφοράς. Τίποτα δεν είναι επιλεγμένο μέχρι να επιλέξετε.',
    restorePointKeptAside:
      '{count} εγγραφές που προστέθηκαν στο μεταξύ φυλάσσονται χωριστά και δεν χάνονται.',
    restorePointSaveFirst: 'Πρώτα αποθηκεύεται ένα σημείο επαναφοράς της σημερινής ημέρας.',
    restorePointConfirm: 'Επαναφορά αυτού του σημείου',
    restorePointBusy: 'Επαναφορά…',
    restorePointNoneChosen: 'Επιλέξτε ένα σημείο επαναφοράς για να συνεχίσετε.',
    versionRestoreTitle: 'Επαναφορά της v{version};',
    versionRestoreLead:
      'Επαναφέρει την εμφάνιση και τη λειτουργία της εφαρμογής όπως στην v{version}. Δεν αλλάζει καμία εγγραφή ή φωτογραφία: παραμένουν όπως είναι.',
    versionRestoreDraft:
      'Γίνεται νέο πρόχειρο. {team} συνεχίζει να χρησιμοποιεί την v{published} μέχρι να κάνετε δημοσίευση.',
    versionRestoreConfirm: 'Επαναφορά της v{version} ως πρόχειρο',
    versionRestoreBusy: 'Επαναφορά…',
    errorGeneric: 'Δεν λειτούργησε. Δεν άλλαξε τίποτα. Δοκιμάστε ξανά.',
    resetUnknown: 'Δεν ήταν δυνατό να επιβεβαιώσουμε αν ολοκληρώθηκε η εκκαθάριση.',
    restorePointUnknown: 'Δεν ήταν δυνατό να επιβεβαιώσουμε αν ολοκληρώθηκε η επαναφορά.',
    restoreVersionUnknown: 'Δεν ήταν δυνατό να επιβεβαιώσουμε αν επαναφέρθηκε η έκδοση {version}.',
    unknownBlockedHint: 'Μπορείτε να δοκιμάσετε ξανά μόλις μάθουμε τι συνέβη.',
    unknownNoCheckHint: 'Φορτώστε ξανά τη σελίδα για να δείτε την τρέχουσα κατάσταση.',
    checkingOutcome: 'Έλεγχος…',
  },
  es: {
    close: 'Cerrar',
    cancel: 'Cancelar',
    resetTitle: '¿Borrar todas las entradas de {app}?',
    resetLead:
      'Solo se borran las entradas y fotos de esta app. Otras apps, {system} y los datos de la empresa no se tocan.',
    resetAudience: '{team} ({people} personas) usa esta app. Verán una lista vacía al instante.',
    resetCleared: 'Se borran {entries} entradas y {photos} fotos.',
    resetRestorePoint:
      'Primero se guarda un punto de restauración. Puedes restaurarlo durante {days} días.',
    resetKeptStays:
      'El historial guardado se mantiene: los recuentos semanales desde {since} no se borran.',
    resetExternalStays: 'Las notas de reclamación ya añadidas en {system} se quedan allí.',
    resetTypeLabel: 'Escribe {app} para confirmar',
    resetOwnerOnly:
      'Solo tú, como propietario, puedes hacerlo, y solo aquí. El chat no puede borrar datos reales.',
    resetNotAllowed:
      'Solo el propietario puede borrar entradas. {owner} puede hacerlo desde esta pestaña.',
    resetConfirm: 'Borrar {entries} entradas',
    resetBusy: 'Borrando…',
    resetDoneTitle: 'Entradas borradas',
    resetDoneBody:
      'Se borraron {entries} entradas y {photos} fotos. Puedes restaurar el punto guardado desde Datos durante {days} días.',
    restorePointsHeading: 'Puntos de restauración',
    restorePointsKept: 'Se guardan {days} días',
    restorePointsNote:
      'Al restaurar, las entradas y fotos vuelven a estar como estaban. Las entradas añadidas después se apartan, no se pierden.',
    restorePointsEmpty:
      'Aún no hay puntos de restauración. Se guarda uno antes de borrar o restaurar nada.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} entradas y {photos} fotos',
    restore: 'Restaurar…',
    restorePointTitle: '¿Restaurar {label}?',
    restorePointLead: 'Elige el punto de restauración. No hay nada seleccionado hasta que elijas.',
    restorePointKeptAside: 'Las {count} entradas añadidas después se apartan, no se pierden.',
    restorePointSaveFirst: 'Primero se guarda un punto de restauración de hoy.',
    restorePointConfirm: 'Restaurar este punto',
    restorePointBusy: 'Restaurando…',
    restorePointNoneChosen: 'Elige un punto de restauración para continuar.',
    versionRestoreTitle: '¿Restaurar v{version}?',
    versionRestoreLead:
      'Esto recupera el aspecto y el funcionamiento de la app en v{version}. No cambia ninguna entrada ni foto: se quedan como están.',
    versionRestoreDraft:
      'Se convierte en un borrador nuevo. {team} sigue usando v{published} hasta que publiques.',
    versionRestoreConfirm: 'Restaurar v{version} como borrador',
    versionRestoreBusy: 'Restaurando…',
    errorGeneric: 'No ha funcionado. No se ha cambiado nada. Inténtalo de nuevo.',
    resetUnknown: 'No pudimos confirmar si el borrado terminó.',
    restorePointUnknown: 'No pudimos confirmar si la restauración terminó.',
    restoreVersionUnknown: 'No pudimos confirmar si se restauró la versión {version}.',
    unknownBlockedHint: 'Podrás volver a intentarlo cuando sepamos qué pasó.',
    unknownNoCheckHint: 'Recarga la página para ver el estado actual.',
    checkingOutcome: 'Comprobando…',
  },
  et: {
    close: 'Sulge',
    cancel: 'Tühista',
    resetTitle: 'Kas tühjendada kõik kirjed rakenduses {app}?',
    resetLead:
      'Tühjendatakse ainult selle rakenduse kirjed ja fotod. Teisi rakendusi, süsteemi {system} ja ettevõtte andmeid see ei puuduta.',
    resetAudience:
      '{team} ({people} inimest) kasutab seda rakendust. Nad näevad kohe tühja loendit.',
    resetCleared: '{entries} kirjet ja {photos} fotot tühjendatakse.',
    resetRestorePoint:
      'Kõigepealt salvestatakse taastepunkt. Saad selle taastada {days} päeva jooksul.',
    resetKeptStays:
      'Säilitatud ajalugu jääb alles: iganädalasi koondarve alates {since} ei tühjendata.',
    resetExternalStays: 'Süsteemi {system} juba lisatud nõuete märkmed jäävad sinna.',
    resetTypeLabel: 'Kinnitamiseks sisesta {app}',
    resetOwnerOnly:
      'Seda saad teha ainult sina omanikuna ja ainult siin. Vestlus ei saa kasutuses olevaid andmeid tühjendada.',
    resetNotAllowed:
      'Kirjeid saab tühjendada ainult omanik. {owner} saab seda teha sellelt vahekaardilt.',
    resetConfirm: 'Tühjenda {entries} kirjet',
    resetBusy: 'Tühjendamine…',
    resetDoneTitle: 'Kirjed on tühjendatud',
    resetDoneBody:
      '{entries} kirjet ja {photos} fotot tühjendati. Taasta salvestatud punkt jaotises Andmed {days} päeva jooksul.',
    restorePointsHeading: 'Taastepunktid',
    restorePointsKept: 'Säilitatakse {days} päeva',
    restorePointsNote:
      'Taastamine toob kirjed ja fotod tagasi sellisena, nagu need olid. Hiljem lisatud kirjed pannakse kõrvale, need ei lähe kaotsi.',
    restorePointsEmpty:
      'Taastepunkte veel pole. Üks salvestatakse enne, kui midagi tühjendatakse või taastatakse.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} kirjet ja {photos} fotot',
    restore: 'Taasta…',
    restorePointTitle: 'Kas taastada {label}?',
    restorePointLead: 'Vali taastepunkt. Midagi pole valitud, kuni sa ise valid.',
    restorePointKeptAside: '{count} hiljem lisatud kirjet pannakse kõrvale, need ei lähe kaotsi.',
    restorePointSaveFirst: 'Kõigepealt salvestatakse tänane taastepunkt.',
    restorePointConfirm: 'Taasta see punkt',
    restorePointBusy: 'Taastamine…',
    restorePointNoneChosen: 'Jätkamiseks vali taastepunkt.',
    versionRestoreTitle: 'Kas taastada v{version}?',
    versionRestoreLead:
      'See toob tagasi rakenduse välimuse ja toimimise versioonist v{version}. Kirjeid ega fotosid see ei muuda: need jäävad nii, nagu on.',
    versionRestoreDraft:
      'Sellest saab uus mustand. {team} kasutab edasi versiooni v{published}, kuni sa avaldad.',
    versionRestoreConfirm: 'Taasta v{version} mustandina',
    versionRestoreBusy: 'Taastamine…',
    errorGeneric: 'See ei õnnestunud. Midagi ei muudetud. Proovi uuesti.',
    resetUnknown: 'Me ei saanud kinnitada, kas tühjendamine lõppes.',
    restorePointUnknown: 'Me ei saanud kinnitada, kas taastamine lõppes.',
    restoreVersionUnknown: 'Me ei saanud kinnitada, kas versioon {version} taastati.',
    unknownBlockedHint: 'Saad uuesti proovida, kui teame, mis juhtus.',
    unknownNoCheckHint: 'Laadi leht uuesti, et näha praegust olekut.',
    checkingOutcome: 'Kontrollimine…',
  },
  fi: {
    close: 'Sulje',
    cancel: 'Peruuta',
    resetTitle: 'Tyhjennetäänkö kaikki merkinnät sovelluksesta {app}?',
    resetLead:
      'Vain tämän sovelluksen merkinnät ja kuvat tyhjennetään. Muihin sovelluksiin, järjestelmään {system} ja yrityksen tietoihin ei kosketa.',
    resetAudience:
      '{team} ({people} henkilöä) käyttää tätä sovellusta. He näkevät tyhjän listan heti.',
    resetCleared: '{entries} merkintää ja {photos} kuvaa tyhjennetään.',
    resetRestorePoint: 'Ensin tallennetaan palautuspiste. Voit palauttaa sen {days} päivän ajan.',
    resetKeptStays: 'Säilytetty historia pysyy: viikoittaisia määriä {since} alkaen ei tyhjennetä.',
    resetExternalStays: 'Järjestelmään {system} jo lisätyt korvausmuistiinpanot pysyvät siellä.',
    resetTypeLabel: 'Vahvista kirjoittamalla {app}',
    resetOwnerOnly:
      'Vain sinä omistajana voit tehdä tämän, ja vain täällä. Chat ei voi tyhjentää käytössä olevia tietoja.',
    resetNotAllowed:
      'Vain omistaja voi tyhjentää merkinnät. {owner} voi tehdä sen tältä välilehdeltä.',
    resetConfirm: 'Tyhjennä {entries} merkintää',
    resetBusy: 'Tyhjennetään…',
    resetDoneTitle: 'Merkinnät tyhjennetty',
    resetDoneBody:
      '{entries} merkintää ja {photos} kuvaa tyhjennettiin. Palauta tallennettu piste kohdasta Tiedot {days} päivän ajan.',
    restorePointsHeading: 'Palautuspisteet',
    restorePointsKept: 'Säilytetään {days} päivää',
    restorePointsNote:
      'Palautus tuo merkinnät ja kuvat takaisin sellaisina kuin ne olivat. Sen jälkeen lisätyt merkinnät siirretään sivuun, eivätkä ne katoa.',
    restorePointsEmpty:
      'Ei vielä palautuspisteitä. Sellainen tallennetaan ennen kuin mitään tyhjennetään tai palautetaan.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} merkintää ja {photos} kuvaa',
    restore: 'Palauta…',
    restorePointTitle: 'Palautetaanko {label}?',
    restorePointLead: 'Valitse palautuspiste. Mitään ei ole valittu ennen kuin valitset.',
    restorePointKeptAside:
      '{count} sen jälkeen lisättyä merkintää siirretään sivuun, eivätkä ne katoa.',
    restorePointSaveFirst: 'Ensin tallennetaan tämän päivän palautuspiste.',
    restorePointConfirm: 'Palauta tämä piste',
    restorePointBusy: 'Palautetaan…',
    restorePointNoneChosen: 'Jatka valitsemalla palautuspiste.',
    versionRestoreTitle: 'Palautetaanko v{version}?',
    versionRestoreLead:
      'Tämä palauttaa sovelluksen ulkoasun ja toiminnan versiosta v{version}. Se ei muuta merkintöjä eikä kuvia: ne pysyvät ennallaan.',
    versionRestoreDraft:
      'Siitä tulee uusi luonnos. {team} käyttää edelleen versiota v{published}, kunnes julkaiset.',
    versionRestoreConfirm: 'Palauta v{version} luonnokseksi',
    versionRestoreBusy: 'Palautetaan…',
    errorGeneric: 'Se ei onnistunut. Mitään ei muutettu. Yritä uudelleen.',
    resetUnknown: 'Emme voineet vahvistaa, valmistuiko tyhjennys.',
    restorePointUnknown: 'Emme voineet vahvistaa, valmistuiko palautus.',
    restoreVersionUnknown: 'Emme voineet vahvistaa, palautettiinko versio {version}.',
    unknownBlockedHint: 'Voit yrittää uudelleen, kun tiedämme, mitä tapahtui.',
    unknownNoCheckHint: 'Lataa sivu uudelleen nähdäksesi nykyisen tilan.',
    checkingOutcome: 'Tarkistetaan…',
  },
  fr: {
    close: 'Fermer',
    cancel: 'Annuler',
    resetTitle: 'Effacer toutes les entrées de {app} ?',
    resetLead:
      'Seules les entrées et les photos de cette app sont effacées. Les autres apps, {system} et les données de l’entreprise ne sont pas touchées.',
    resetAudience:
      '{team} ({people} personnes) utilise cette app. Ses membres verront aussitôt une liste vide.',
    resetCleared: '{entries} entrées et {photos} photos sont effacées.',
    resetRestorePoint:
      'Un point de restauration est d’abord enregistré. Vous pouvez le restaurer pendant {days} jours.',
    resetKeptStays:
      'L’historique conservé reste : les totaux hebdomadaires depuis le {since} ne sont pas effacés.',
    resetExternalStays: 'Les notes de sinistre déjà ajoutées dans {system} y restent.',
    resetTypeLabel: 'Saisissez {app} pour confirmer',
    resetOwnerOnly:
      'Seul vous, en tant que propriétaire, pouvez le faire, et seulement ici. Le chat ne peut pas effacer les données réelles.',
    resetNotAllowed:
      'Seul le propriétaire peut effacer les entrées. {owner} peut le faire depuis cet onglet.',
    resetConfirm: 'Effacer {entries} entrées',
    resetBusy: 'Effacement…',
    resetDoneTitle: 'Entrées effacées',
    resetDoneBody:
      '{entries} entrées et {photos} photos ont été effacées. Vous pouvez restaurer le point enregistré depuis Données pendant {days} jours.',
    restorePointsHeading: 'Points de restauration',
    restorePointsKept: 'Conservés {days} jours',
    restorePointsNote:
      'La restauration rétablit les entrées et les photos telles qu’elles étaient. Les entrées ajoutées depuis sont mises de côté, pas perdues.',
    restorePointsEmpty:
      'Aucun point de restauration pour l’instant. Un point est enregistré avant chaque effacement ou restauration.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} entrées et {photos} photos',
    restore: 'Restaurer…',
    restorePointTitle: 'Restaurer {label} ?',
    restorePointLead:
      'Choisissez le point de restauration. Rien n’est sélectionné tant que vous n’avez pas choisi.',
    restorePointKeptAside: '{count} entrées ajoutées depuis sont mises de côté, pas perdues.',
    restorePointSaveFirst: 'Un point de restauration d’aujourd’hui est d’abord enregistré.',
    restorePointConfirm: 'Restaurer ce point',
    restorePointBusy: 'Restauration…',
    restorePointNoneChosen: 'Choisissez un point de restauration pour continuer.',
    versionRestoreTitle: 'Restaurer v{version} ?',
    versionRestoreLead:
      'L’app retrouve l’apparence et le fonctionnement de v{version}. Aucune entrée ni photo n’est modifiée : elles restent telles quelles.',
    versionRestoreDraft:
      'Elle devient un nouveau brouillon. {team} continue d’utiliser v{published} jusqu’à ce que vous publiiez.',
    versionRestoreConfirm: 'Restaurer v{version} comme brouillon',
    versionRestoreBusy: 'Restauration…',
    errorGeneric: 'Cela n’a pas fonctionné. Rien n’a été modifié. Réessayez.',
    resetUnknown: 'Nous n’avons pas pu confirmer la fin de l’effacement.',
    restorePointUnknown: 'Nous n’avons pas pu confirmer la fin de la restauration.',
    restoreVersionUnknown: 'Nous n’avons pas pu confirmer la restauration de la version {version}.',
    unknownBlockedHint: 'Vous pourrez réessayer dès que nous saurons ce qui s’est passé.',
    unknownNoCheckHint: 'Rechargez la page pour voir l’état actuel.',
    checkingOutcome: 'Vérification…',
  },
  ga: {
    close: 'Dún',
    cancel: 'Cealaigh',
    resetTitle: 'Glan na hiontrálacha go léir in {app}?',
    resetLead:
      'Ní ghlantar ach iontrálacha agus grianghraif na haipe seo. Ní bhaintear le haipeanna eile, le {system} ná le sonraí an chomhlachta.',
    resetAudience:
      'Úsáideann {team} (daoine: {people}) an aip seo. Feicfidh siad liosta folamh láithreach.',
    resetCleared: 'Glantar na hiontrálacha ({entries}) agus na grianghraif ({photos}).',
    resetRestorePoint:
      'Sábháiltear pointe athchóirithe ar dtús. Is féidir leat é a athchóiriú le linn na tréimhse coinneála (laethanta: {days}).',
    resetKeptStays:
      'Fanann an stair choinnithe: ní ghlantar na comhairimh sheachtainiúla ó {since}.',
    resetExternalStays: 'Fanann nótaí éilimh atá curtha le {system} cheana ansin.',
    resetTypeLabel: 'Clóscríobh {app} chun é a dhearbhú',
    resetOwnerOnly:
      'Níl ach tusa, mar úinéir, in ann é seo a dhéanamh, agus anseo amháin. Ní féidir leis an gcomhrá sonraí beo a ghlanadh.',
    resetNotAllowed:
      'Níl ach an t-úinéir in ann iontrálacha a ghlanadh. Is féidir le {owner} é seo a dhéanamh ón gcluaisín seo.',
    resetConfirm: 'Glan na hiontrálacha ({entries})',
    resetBusy: 'Á ghlanadh…',
    resetDoneTitle: 'Glanadh na hiontrálacha',
    resetDoneBody:
      'Glanadh na hiontrálacha ({entries}) agus na grianghraif ({photos}). Is féidir leat an pointe sábháilte a athchóiriú ó Shonraí le linn na tréimhse coinneála (laethanta: {days}).',
    restorePointsHeading: 'Pointí athchóirithe',
    restorePointsKept: 'Laethanta coinneála: {days}',
    restorePointsNote:
      'Tugann athchóiriú na hiontrálacha agus na grianghraif ar ais mar a bhí siad. Cuirtear iontrálacha a cuireadh leis ó shin i leataobh, ní chailltear iad.',
    restorePointsEmpty:
      'Níl aon phointe athchóirithe ann fós. Sábháiltear ceann sula nglantar nó sula n-athchóirítear aon rud.',
    restorePointRow: '{label}',
    restorePointCounts: 'Iontrálacha: {entries}, grianghraif: {photos}',
    restore: 'Athchóirigh…',
    restorePointTitle: 'Athchóirigh {label}?',
    restorePointLead:
      'Roghnaigh an pointe athchóirithe. Níl aon rud roghnaithe go dtí go roghnaíonn tú.',
    restorePointKeptAside:
      'Cuirtear iontrálacha a cuireadh leis ó shin ({count}) i leataobh, ní chailltear iad.',
    restorePointSaveFirst: 'Sábháiltear pointe athchóirithe den lá inniu ar dtús.',
    restorePointConfirm: 'Athchóirigh an pointe seo',
    restorePointBusy: 'Á athchóiriú…',
    restorePointNoneChosen: 'Roghnaigh pointe athchóirithe chun leanúint ar aghaidh.',
    versionRestoreTitle: 'Athchóirigh v{version}?',
    versionRestoreLead:
      'Tugann sé seo cuma agus feidhmiú na haipe in v{version} ar ais. Ní athraíonn sé aon iontráil ná grianghraf: fanann siad mar atá.',
    versionRestoreDraft:
      'Beidh sé ina dhréacht nua. Leanfaidh {team} ag úsáid v{published} go dtí go bhfoilsíonn tú é.',
    versionRestoreConfirm: 'Athchóirigh v{version} mar dhréacht',
    versionRestoreBusy: 'Á athchóiriú…',
    errorGeneric: 'Níor oibrigh sé sin. Níor athraíodh aon rud. Bain triail eile as.',
    resetUnknown: 'Níorbh fhéidir linn a dhearbhú ar chríochnaigh an glanadh.',
    restorePointUnknown: 'Níorbh fhéidir linn a dhearbhú ar chríochnaigh an t-athchóiriú.',
    restoreVersionUnknown: 'Níorbh fhéidir linn a dhearbhú ar athchóiríodh leagan {version}.',
    unknownBlockedHint:
      'Is féidir leat triail eile a bhaint as nuair a bheidh a fhios againn cad a tharla.',
    unknownNoCheckHint: 'Athlódáil an leathanach chun an staid reatha a fheiceáil.',
    checkingOutcome: 'Á sheiceáil…',
  },
  hr: {
    close: 'Zatvori',
    cancel: 'Odustani',
    resetTitle: 'Izbrisati sve unose u aplikaciji {app}?',
    resetLead:
      'Brišu se samo unosi i fotografije ove aplikacije. Ostale aplikacije, {system} i podaci tvrtke ostaju netaknuti.',
    resetAudience:
      'Ovu aplikaciju koristi {team} (broj osoba: {people}). Odmah će vidjeti prazan popis.',
    resetCleared: 'Briše se – unosi: {entries}, fotografije: {photos}.',
    resetRestorePoint:
      'Najprije se sprema točka vraćanja. Možete je vratiti u ograničenom roku (broj dana: {days}).',
    resetKeptStays: 'Sačuvana povijest ostaje: tjedni zbrojevi od {since} ne brišu se.',
    resetExternalStays: 'Bilješke o zahtjevima već dodane u {system} ostaju ondje.',
    resetTypeLabel: 'Upišite {app} za potvrdu',
    resetOwnerOnly:
      'To možete učiniti samo vi kao vlasnik, i to samo ovdje. Chat ne može brisati podatke uživo.',
    resetNotAllowed: 'Unose može izbrisati samo vlasnik. {owner} to može učiniti na ovoj kartici.',
    resetConfirm: 'Izbriši unose: {entries}',
    resetBusy: 'Brisanje…',
    resetDoneTitle: 'Unosi su izbrisani',
    resetDoneBody:
      'Izbrisano – unosi: {entries}, fotografije: {photos}. Spremljenu točku možete vratiti u odjeljku Podaci u ograničenom roku (broj dana: {days}).',
    restorePointsHeading: 'Točke vraćanja',
    restorePointsKept: 'Čuva se (broj dana): {days}',
    restorePointsNote:
      'Vraćanje vraća unose i fotografije u prijašnje stanje. Unosi dodani u međuvremenu odlažu se na stranu, ne gube se.',
    restorePointsEmpty:
      'Još nema točaka vraćanja. Jedna se sprema prije svakog brisanja ili vraćanja.',
    restorePointRow: '{label}',
    restorePointCounts: 'Unosi: {entries}, fotografije: {photos}',
    restore: 'Vrati…',
    restorePointTitle: 'Vratiti {label}?',
    restorePointLead: 'Odaberite točku vraćanja. Ništa nije odabrano dok ne odaberete.',
    restorePointKeptAside:
      'Unosi dodani u međuvremenu (broj: {count}) odlažu se na stranu, ne gube se.',
    restorePointSaveFirst: 'Najprije se sprema današnja točka vraćanja.',
    restorePointConfirm: 'Vrati ovu točku',
    restorePointBusy: 'Vraćanje…',
    restorePointNoneChosen: 'Za nastavak odaberite točku vraćanja.',
    versionRestoreTitle: 'Vratiti v{version}?',
    versionRestoreLead:
      'Time se vraćaju izgled i rad aplikacije iz v{version}. Ne mijenjaju se nikakvi unosi ni fotografije: ostaju kakvi jesu.',
    versionRestoreDraft: 'Postaje novi nacrt. {team} i dalje koristi v{published} dok ne objavite.',
    versionRestoreConfirm: 'Vrati v{version} kao nacrt',
    versionRestoreBusy: 'Vraćanje…',
    errorGeneric: 'Nije uspjelo. Ništa nije promijenjeno. Pokušajte ponovno.',
    resetUnknown: 'Nismo mogli potvrditi je li brisanje završeno.',
    restorePointUnknown: 'Nismo mogli potvrditi je li vraćanje završeno.',
    restoreVersionUnknown: 'Nismo mogli potvrditi je li verzija {version} vraćena.',
    unknownBlockedHint: 'Možete pokušati ponovno kad saznamo što se dogodilo.',
    unknownNoCheckHint: 'Ponovno učitajte stranicu da vidite trenutačno stanje.',
    checkingOutcome: 'Provjera…',
  },
  hu: {
    close: 'Bezárás',
    cancel: 'Mégse',
    resetTitle: 'Törli az összes bejegyzést a(z) {app} alkalmazásban?',
    resetLead:
      'Csak ennek az alkalmazásnak a bejegyzései és fotói törlődnek. Más alkalmazásokat, a(z) {system} rendszert és a céges adatokat ez nem érinti.',
    resetAudience:
      'Ezt az alkalmazást {team} használja ({people} fő). Azonnal üres listát fognak látni.',
    resetCleared: '{entries} bejegyzés és {photos} fotó törlődik.',
    resetRestorePoint:
      'Előbb mentünk egy visszaállítási pontot, amelyet {days} napig visszaállíthat.',
    resetKeptStays:
      'A megőrzött előzmények maradnak: a heti összesítéseket ({since} óta) nem töröljük.',
    resetExternalStays: 'A(z) {system} rendszerbe már felvett kárügyi jegyzetek ott maradnak.',
    resetTypeLabel: 'A megerősítéshez írja be: {app}',
    resetOwnerOnly:
      'Ezt csak Ön teheti meg tulajdonosként, és csak itt. A csevegés nem törölhet élő adatokat.',
    resetNotAllowed:
      'Csak a tulajdonos törölhet bejegyzéseket. {owner} ezt ezen a lapon teheti meg.',
    resetConfirm: '{entries} bejegyzés törlése',
    resetBusy: 'Törlés…',
    resetDoneTitle: 'Bejegyzések törölve',
    resetDoneBody:
      '{entries} bejegyzés és {photos} fotó törölve. A mentett pontot {days} napig állíthatja vissza az Adatok lapon.',
    restorePointsHeading: 'Visszaállítási pontok',
    restorePointsKept: '{days} napig megőrizve',
    restorePointsNote:
      'A visszaállítás a bejegyzéseket és fotókat a korábbi állapotukban hozza vissza. Az azóta felvett bejegyzéseket félretesszük, nem vesznek el.',
    restorePointsEmpty:
      'Még nincs visszaállítási pont. Minden törlés vagy visszaállítás előtt mentünk egyet.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} bejegyzés és {photos} fotó',
    restore: 'Visszaállítás…',
    restorePointTitle: 'Visszaállítja ezt: {label}?',
    restorePointLead:
      'Válassza ki a visszaállítási pontot. Amíg nem választ, semmi sincs kijelölve.',
    restorePointKeptAside: 'Az azóta felvett {count} bejegyzést félretesszük, nem vesznek el.',
    restorePointSaveFirst: 'Előbb mentünk egy mai visszaállítási pontot.',
    restorePointConfirm: 'Ennek a pontnak a visszaállítása',
    restorePointBusy: 'Visszaállítás…',
    restorePointNoneChosen: 'A folytatáshoz válasszon visszaállítási pontot.',
    versionRestoreTitle: 'Visszaállítja a v{version} verziót?',
    versionRestoreLead:
      'Ez visszahozza, hogyan nézett ki és működött az alkalmazás a v{version} verzióban. Egyetlen bejegyzést vagy fotót sem módosít: ezek maradnak, ahogy vannak.',
    versionRestoreDraft:
      'Ebből új piszkozat lesz. {team} a közzétételig továbbra is a v{published} verziót használja.',
    versionRestoreConfirm: 'v{version} visszaállítása piszkozatként',
    versionRestoreBusy: 'Visszaállítás…',
    errorGeneric: 'Ez nem sikerült. Semmi sem változott. Próbálja újra.',
    resetUnknown: 'Nem tudtuk megerősíteni, hogy a törlés befejeződött-e.',
    restorePointUnknown: 'Nem tudtuk megerősíteni, hogy a visszaállítás befejeződött-e.',
    restoreVersionUnknown: 'Nem tudtuk megerősíteni, hogy a(z) {version}. verzió visszaállt-e.',
    unknownBlockedHint: 'Újra megpróbálhatja, amint tudjuk, mi történt.',
    unknownNoCheckHint: 'Töltse újra az oldalt a jelenlegi állapot megtekintéséhez.',
    checkingOutcome: 'Ellenőrzés…',
  },
  it: {
    close: 'Chiudi',
    cancel: 'Annulla',
    resetTitle: 'Cancellare tutte le voci di {app}?',
    resetLead:
      'Vengono cancellate solo le voci e le foto di questa app. Le altre app, {system} e i dati aziendali restano intatti.',
    resetAudience: '{team} ({people} persone) usa questa app. Vedranno subito un elenco vuoto.',
    resetCleared: 'Vengono cancellate {entries} voci e {photos} foto.',
    resetRestorePoint:
      'Prima viene salvato un punto di ripristino. Puoi ripristinarlo per {days} giorni.',
    resetKeptStays:
      'Lo storico conservato resta: i conteggi settimanali dal {since} non vengono cancellati.',
    resetExternalStays: 'Le note sui sinistri già aggiunte in {system} restano lì.',
    resetTypeLabel: 'Digita {app} per confermare',
    resetOwnerOnly:
      'Solo tu, come proprietario, puoi farlo, e solo qui. La chat non può cancellare i dati reali.',
    resetNotAllowed:
      'Solo il proprietario può cancellare le voci. {owner} può farlo da questa scheda.',
    resetConfirm: 'Cancella {entries} voci',
    resetBusy: 'Cancellazione…',
    resetDoneTitle: 'Voci cancellate',
    resetDoneBody:
      'Sono state cancellate {entries} voci e {photos} foto. Puoi ripristinare il punto salvato da Dati per {days} giorni.',
    restorePointsHeading: 'Punti di ripristino',
    restorePointsKept: 'Conservati per {days} giorni',
    restorePointsNote:
      'Il ripristino riporta voci e foto com’erano. Le voci aggiunte nel frattempo vengono messe da parte, non perse.',
    restorePointsEmpty:
      'Ancora nessun punto di ripristino. Ne viene salvato uno prima di ogni cancellazione o ripristino.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} voci e {photos} foto',
    restore: 'Ripristina…',
    restorePointTitle: 'Ripristinare {label}?',
    restorePointLead: 'Scegli il punto di ripristino. Non è selezionato nulla finché non scegli.',
    restorePointKeptAside: '{count} voci aggiunte nel frattempo vengono messe da parte, non perse.',
    restorePointSaveFirst: 'Prima viene salvato un punto di ripristino di oggi.',
    restorePointConfirm: 'Ripristina questo punto',
    restorePointBusy: 'Ripristino…',
    restorePointNoneChosen: 'Scegli un punto di ripristino per continuare.',
    versionRestoreTitle: 'Ripristinare v{version}?',
    versionRestoreLead:
      'L’app torna all’aspetto e al funzionamento di v{version}. Nessuna voce o foto cambia: restano come sono.',
    versionRestoreDraft:
      'Diventa una nuova bozza. {team} continua a usare v{published} finché non pubblichi.',
    versionRestoreConfirm: 'Ripristina v{version} come bozza',
    versionRestoreBusy: 'Ripristino…',
    errorGeneric: 'Non ha funzionato. Non è stato modificato nulla. Riprova.',
    resetUnknown: 'Non siamo riusciti a confermare se la cancellazione è terminata.',
    restorePointUnknown: 'Non siamo riusciti a confermare se il ripristino è terminato.',
    restoreVersionUnknown:
      'Non siamo riusciti a confermare se la versione {version} è stata ripristinata.',
    unknownBlockedHint: 'Potrai riprovare quando sapremo cosa è successo.',
    unknownNoCheckHint: 'Ricarica la pagina per vedere lo stato attuale.',
    checkingOutcome: 'Verifica in corso…',
  },
  lt: {
    close: 'Uždaryti',
    cancel: 'Atšaukti',
    resetTitle: 'Išvalyti visus programėlės {app} įrašus?',
    resetLead:
      'Išvalomi tik šios programėlės įrašai ir nuotraukos. Kitos programėlės, {system} ir įmonės duomenys nepaliečiami.',
    resetAudience:
      'Šią programėlę naudoja {team} (žmonių: {people}). Jie iš karto matys tuščią sąrašą.',
    resetCleared: 'Išvaloma įrašų: {entries}, nuotraukų: {photos}.',
    resetRestorePoint: 'Pirmiausia išsaugomas atkūrimo taškas. Jį atkurti galėsite {days} d.',
    resetKeptStays: 'Išsaugota istorija lieka: savaitės suvestinės nuo {since} neišvalomos.',
    resetExternalStays: 'Pretenzijų pastabos, jau pridėtos sistemoje {system}, ten ir lieka.',
    resetTypeLabel: 'Įveskite {app}, kad patvirtintumėte',
    resetOwnerOnly:
      'Tai galite padaryti tik jūs, kaip savininkas, ir tik čia. Pokalbis negali išvalyti tikrų duomenų.',
    resetNotAllowed:
      'Įrašus išvalyti gali tik savininkas. {owner} gali tai padaryti šiame skirtuke.',
    resetConfirm: 'Išvalyti įrašus ({entries})',
    resetBusy: 'Valoma…',
    resetDoneTitle: 'Įrašai išvalyti',
    resetDoneBody:
      'Išvalyta įrašų: {entries}, nuotraukų: {photos}. Išsaugotą tašką skiltyje „Duomenys“ galite atkurti {days} d.',
    restorePointsHeading: 'Atkūrimo taškai',
    restorePointsKept: 'Saugoma {days} d.',
    restorePointsNote:
      'Atkūrus grąžinami įrašai ir nuotraukos tokie, kokie buvo. Vėliau pridėti įrašai atidedami į šalį, o ne prarandami.',
    restorePointsEmpty:
      'Atkūrimo taškų dar nėra. Taškas išsaugomas prieš ką nors išvalant ar atkuriant.',
    restorePointRow: '{label}',
    restorePointCounts: 'Įrašai: {entries}, nuotraukos: {photos}',
    restore: 'Atkurti…',
    restorePointTitle: 'Atkurti {label}?',
    restorePointLead: 'Pasirinkite atkūrimo tašką. Kol nepasirinksite, niekas nepažymėta.',
    restorePointKeptAside: 'Vėliau pridėti įrašai ({count}) atidedami į šalį, o ne prarandami.',
    restorePointSaveFirst: 'Pirmiausia išsaugomas šiandienos atkūrimo taškas.',
    restorePointConfirm: 'Atkurti šį tašką',
    restorePointBusy: 'Atkuriama…',
    restorePointNoneChosen: 'Pasirinkite atkūrimo tašką, kad galėtumėte tęsti.',
    versionRestoreTitle: 'Atkurti v{version}?',
    versionRestoreLead:
      'Programėlė vėl atrodys ir veiks kaip v{version}. Jokie įrašai ar nuotraukos nekeičiami: jie lieka tokie, kokie yra.',
    versionRestoreDraft:
      'Ji taps nauju juodraščiu. {team} ir toliau naudos v{published}, kol paskelbsite.',
    versionRestoreConfirm: 'Atkurti v{version} kaip juodraštį',
    versionRestoreBusy: 'Atkuriama…',
    errorGeneric: 'Nepavyko. Niekas nepakeista. Bandykite dar kartą.',
    resetUnknown: 'Nepavyko patvirtinti, ar išvalymas baigtas.',
    restorePointUnknown: 'Nepavyko patvirtinti, ar atkūrimas baigtas.',
    restoreVersionUnknown: 'Nepavyko patvirtinti, ar {version} versija atkurta.',
    unknownBlockedHint: 'Galėsite bandyti dar kartą, kai sužinosime, kas nutiko.',
    unknownNoCheckHint: 'Įkelkite puslapį iš naujo, kad pamatytumėte dabartinę būseną.',
    checkingOutcome: 'Tikrinama…',
  },
  lv: {
    close: 'Aizvērt',
    cancel: 'Atcelt',
    resetTitle: 'Notīrīt visus ierakstus lietotnē {app}?',
    resetLead:
      'Tiek notīrīti tikai šīs lietotnes ieraksti un fotoattēli. Citas lietotnes, {system} un uzņēmuma dati netiek skarti.',
    resetAudience:
      'Šo lietotni izmanto {team} (cilvēki: {people}). Viņi uzreiz redzēs tukšu sarakstu.',
    resetCleared: 'Tiek notīrīti ieraksti ({entries}) un fotoattēli ({photos}).',
    resetRestorePoint:
      'Vispirms tiek saglabāts atjaunošanas punkts. To varēsiet atjaunot {days} d. laikā.',
    resetKeptStays: 'Saglabātā vēsture paliek: iknedēļas skaiti kopš {since} netiek notīrīti.',
    resetExternalStays: 'Pretenziju piezīmes, kas jau pievienotas sistēmā {system}, paliek tur.',
    resetTypeLabel: 'Ierakstiet {app}, lai apstiprinātu',
    resetOwnerOnly:
      'To varat izdarīt tikai jūs kā īpašnieks un tikai šeit. Tērzēšana nevar notīrīt reāllaika datus.',
    resetNotAllowed: 'Ierakstus var notīrīt tikai īpašnieks. {owner} to var izdarīt šajā cilnē.',
    resetConfirm: 'Notīrīt ierakstus ({entries})',
    resetBusy: 'Notīra…',
    resetDoneTitle: 'Ieraksti notīrīti',
    resetDoneBody:
      'Ieraksti ({entries}) un fotoattēli ({photos}) tika notīrīti. Saglabāto punktu sadaļā „Dati” varat atjaunot {days} d. laikā.',
    restorePointsHeading: 'Atjaunošanas punkti',
    restorePointsKept: 'Glabā {days} d.',
    restorePointsNote:
      'Atjaunošana atgriež ierakstus un fotoattēlus tādus, kādi tie bija. Vēlāk pievienotie ieraksti tiek nolikti malā, nevis zaudēti.',
    restorePointsEmpty:
      'Atjaunošanas punktu vēl nav. Punkts tiek saglabāts, pirms kaut kas tiek notīrīts vai atjaunots.',
    restorePointRow: '{label}',
    restorePointCounts: 'Ieraksti: {entries}, fotoattēli: {photos}',
    restore: 'Atjaunot…',
    restorePointTitle: 'Atjaunot {label}?',
    restorePointLead: 'Izvēlieties atjaunošanas punktu. Kamēr neizvēlēsieties, nekas nav atlasīts.',
    restorePointKeptAside: 'Vēlāk pievienotie ieraksti ({count}) tiek nolikti malā, nevis zaudēti.',
    restorePointSaveFirst: 'Vispirms tiek saglabāts šodienas atjaunošanas punkts.',
    restorePointConfirm: 'Atjaunot šo punktu',
    restorePointBusy: 'Atjauno…',
    restorePointNoneChosen: 'Lai turpinātu, izvēlieties atjaunošanas punktu.',
    versionRestoreTitle: 'Atjaunot v{version}?',
    versionRestoreLead:
      'Lietotne atkal izskatīsies un darbosies tāpat kā v{version}. Tas nemaina nevienu ierakstu vai fotoattēlu: tie paliek, kā ir.',
    versionRestoreDraft:
      'Tā kļūs par jaunu melnrakstu. {team} turpinās izmantot v{published}, līdz jūs publicēsiet.',
    versionRestoreConfirm: 'Atjaunot v{version} kā melnrakstu',
    versionRestoreBusy: 'Atjauno…',
    errorGeneric: 'Neizdevās. Nekas netika mainīts. Mēģiniet vēlreiz.',
    resetUnknown: 'Mēs nevarējām apstiprināt, vai notīrīšana tika pabeigta.',
    restorePointUnknown: 'Mēs nevarējām apstiprināt, vai atjaunošana tika pabeigta.',
    restoreVersionUnknown: 'Mēs nevarējām apstiprināt, vai versija {version} tika atjaunota.',
    unknownBlockedHint: 'Varēsiet mēģināt vēlreiz, kad zināsim, kas notika.',
    unknownNoCheckHint: 'Pārlādējiet lapu, lai redzētu pašreizējo stāvokli.',
    checkingOutcome: 'Pārbauda…',
  },
  mt: {
    close: 'Agħlaq',
    cancel: 'Ikkanċella',
    resetTitle: 'Tnaddaf l-entrati kollha f’{app}?',
    resetLead:
      'Jitnaddfu biss l-entrati u r-ritratti ta’ din l-app. App oħra, {system} u d-data tal-kumpanija ma jintmessux.',
    resetAudience: '{team} (nies: {people}) juża din l-app. Se jaraw lista vojta mill-ewwel.',
    resetCleared: 'Jitnaddfu l-entrati ({entries}) u r-ritratti ({photos}).',
    resetRestorePoint:
      'L-ewwel jiġi ssejvjat punt ta’ restawr. Tista’ tirrestawrah matul il-perjodu taż-żamma (jiem: {days}).',
    resetKeptStays: 'L-istorja miżmuma tibqa’: l-għadd ta’ kull ġimgħa minn {since} ma jitnaddafx.',
    resetExternalStays: 'In-noti tal-pretensjonijiet diġà miżjuda f’{system} jibqgħu hemm.',
    resetTypeLabel: 'Ikteb {app} biex tikkonferma',
    resetOwnerOnly:
      'Int biss, bħala s-sid, tista’ tagħmel dan, u hawn biss. Iċ-chat ma jistax inaddaf data live.',
    resetNotAllowed:
      'Is-sid biss jista’ jnaddaf l-entrati. {owner} jista’ jagħmel dan minn din it-tab.',
    resetConfirm: 'Naddaf l-entrati ({entries})',
    resetBusy: 'Qed jitnaddaf…',
    resetDoneTitle: 'L-entrati tnaddfu',
    resetDoneBody:
      'L-entrati ({entries}) u r-ritratti ({photos}) tnaddfu. Tista’ tirrestawra l-punt issejvjat minn Data matul il-perjodu taż-żamma (jiem: {days}).',
    restorePointsHeading: 'Punti ta’ restawr',
    restorePointsKept: 'Jiem taż-żamma: {days}',
    restorePointsNote:
      'Ir-restawr iġib lura l-entrati u r-ritratti kif kienu. L-entrati miżjuda minn dakinhar jitwarbu, ma jintilfux.',
    restorePointsEmpty:
      'Għad m’hemmx punti ta’ restawr. Wieħed jiġi ssejvjat qabel ma xi ħaġa titnaddaf jew tiġi restawrata.',
    restorePointRow: '{label}',
    restorePointCounts: 'Entrati: {entries}, ritratti: {photos}',
    restore: 'Irrestawra…',
    restorePointTitle: 'Tirrestawra {label}?',
    restorePointLead: 'Agħżel il-punt ta’ restawr. Xejn mhu magħżul sakemm tagħżel.',
    restorePointKeptAside: 'L-entrati miżjuda minn dakinhar ({count}) jitwarbu, ma jintilfux.',
    restorePointSaveFirst: 'L-ewwel jiġi ssejvjat punt ta’ restawr tal-lum.',
    restorePointConfirm: 'Irrestawra dan il-punt',
    restorePointBusy: 'Qed jiġi restawrat…',
    restorePointNoneChosen: 'Agħżel punt ta’ restawr biex tkompli.',
    versionRestoreTitle: 'Tirrestawra v{version}?',
    versionRestoreLead:
      'Dan iġib lura kif l-app tidher u taħdem f’v{version}. Ma jibdel l-ebda entrata jew ritratt: dawk jibqgħu kif inhuma.',
    versionRestoreDraft: 'Issir abbozz ġdid. {team} jibqa’ juża v{published} sakemm tippubblika.',
    versionRestoreConfirm: 'Irrestawra v{version} bħala abbozz',
    versionRestoreBusy: 'Qed jiġi restawrat…',
    errorGeneric: 'Ma ħadimx. Xejn ma nbidel. Erġa’ pprova.',
    resetUnknown: 'Ma stajniex nikkonfermaw jekk it-tindif intemmx.',
    restorePointUnknown: 'Ma stajniex nikkonfermaw jekk ir-restawr intemmx.',
    restoreVersionUnknown: 'Ma stajniex nikkonfermaw jekk il-verżjoni {version} ġietx restawrata.',
    unknownBlockedHint: 'Tista’ terġa’ tipprova ladarba nkunu nafu x’ġara.',
    unknownNoCheckHint: 'Erġa’ tella’ l-paġna biex tara l-istat attwali.',
    checkingOutcome: 'Qed niċċekkjaw…',
  },
  nl: {
    close: 'Sluiten',
    cancel: 'Annuleren',
    resetTitle: 'Alle items in {app} wissen?',
    resetLead:
      'Alleen de items en foto’s van deze app worden gewist. Andere apps, {system} en bedrijfsgegevens blijven onaangeroerd.',
    resetAudience: '{team} ({people} personen) gebruikt deze app. Zij zien meteen een lege lijst.',
    resetCleared: '{entries} items en {photos} foto’s worden gewist.',
    resetRestorePoint:
      'Eerst wordt een herstelpunt opgeslagen. Je kunt het {days} dagen lang herstellen.',
    resetKeptStays: 'Bewaarde geschiedenis blijft: weektotalen sinds {since} worden niet gewist.',
    resetExternalStays: 'Claimnotities die al in {system} zijn toegevoegd, blijven daar.',
    resetTypeLabel: 'Typ {app} om te bevestigen',
    resetOwnerOnly:
      'Alleen jij, als eigenaar, kunt dit doen, en alleen hier. De chat kan geen live-gegevens wissen.',
    resetNotAllowed: 'Alleen de eigenaar kan items wissen. {owner} kan dit doen vanaf dit tabblad.',
    resetConfirm: '{entries} items wissen',
    resetBusy: 'Bezig met wissen…',
    resetDoneTitle: 'Items gewist',
    resetDoneBody:
      '{entries} items en {photos} foto’s zijn gewist. Je kunt het opgeslagen punt {days} dagen lang herstellen via Gegevens.',
    restorePointsHeading: 'Herstelpunten',
    restorePointsKept: '{days} dagen bewaard',
    restorePointsNote:
      'Herstellen brengt items en foto’s terug zoals ze waren. Items die sindsdien zijn toegevoegd, worden apart gezet, niet verwijderd.',
    restorePointsEmpty:
      'Nog geen herstelpunten. Er wordt er een opgeslagen voordat er iets wordt gewist of hersteld.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} items en {photos} foto’s',
    restore: 'Herstellen…',
    restorePointTitle: '{label} herstellen?',
    restorePointLead: 'Kies het herstelpunt. Er is niets geselecteerd totdat je kiest.',
    restorePointKeptAside:
      '{count} items die sindsdien zijn toegevoegd, worden apart gezet, niet verwijderd.',
    restorePointSaveFirst: 'Eerst wordt een herstelpunt van vandaag opgeslagen.',
    restorePointConfirm: 'Dit punt herstellen',
    restorePointBusy: 'Bezig met herstellen…',
    restorePointNoneChosen: 'Kies een herstelpunt om door te gaan.',
    versionRestoreTitle: 'v{version} herstellen?',
    versionRestoreLead:
      'Hiermee komt terug hoe de app eruitziet en werkt in v{version}. Er veranderen geen items of foto’s: die blijven zoals ze zijn.',
    versionRestoreDraft:
      'Het wordt een nieuw concept. {team} blijft v{published} gebruiken totdat je publiceert.',
    versionRestoreConfirm: 'v{version} herstellen als concept',
    versionRestoreBusy: 'Bezig met herstellen…',
    errorGeneric: 'Dat is niet gelukt. Er is niets gewijzigd. Probeer het opnieuw.',
    resetUnknown: 'We konden niet bevestigen of het wissen is voltooid.',
    restorePointUnknown: 'We konden niet bevestigen of het herstellen is voltooid.',
    restoreVersionUnknown: 'We konden niet bevestigen of versie {version} is hersteld.',
    unknownBlockedHint: 'Je kunt het opnieuw proberen zodra we weten wat er is gebeurd.',
    unknownNoCheckHint: 'Laad de pagina opnieuw om de huidige stand te zien.',
    checkingOutcome: 'Bezig met controleren…',
  },
  pl: {
    close: 'Zamknij',
    cancel: 'Anuluj',
    resetTitle: 'Wyczyścić wszystkie wpisy w {app}?',
    resetLead:
      'Czyszczone są tylko wpisy i zdjęcia tej aplikacji. Inne aplikacje, {system} i dane firmy pozostają nietknięte.',
    resetAudience:
      'Z tej aplikacji korzysta {team} (liczba osób: {people}). Od razu zobaczą pustą listę.',
    resetCleared: 'Zostaną wyczyszczone – wpisy: {entries}, zdjęcia: {photos}.',
    resetRestorePoint:
      'Najpierw zapisywany jest punkt przywracania. Możesz go przywrócić przez ograniczony czas (liczba dni: {days}).',
    resetKeptStays:
      'Zachowana historia pozostaje: tygodniowe podsumowania od {since} nie są czyszczone.',
    resetExternalStays: 'Notatki do roszczeń dodane już w {system} pozostają tam.',
    resetTypeLabel: 'Wpisz {app}, aby potwierdzić',
    resetOwnerOnly:
      'Tylko Ty, jako właściciel, możesz to zrobić, i tylko tutaj. Czat nie może czyścić bieżących danych.',
    resetNotAllowed: 'Tylko właściciel może czyścić wpisy. {owner} może to zrobić na tej karcie.',
    resetConfirm: 'Wyczyść wpisy: {entries}',
    resetBusy: 'Czyszczenie…',
    resetDoneTitle: 'Wpisy wyczyszczone',
    resetDoneBody:
      'Wyczyszczono – wpisy: {entries}, zdjęcia: {photos}. Zapisany punkt możesz przywrócić w sekcji Dane przez ograniczony czas (liczba dni: {days}).',
    restorePointsHeading: 'Punkty przywracania',
    restorePointsKept: 'Przechowywane (liczba dni): {days}',
    restorePointsNote:
      'Po przywróceniu wpisy i zdjęcia wracają do stanu z tamtej chwili. Wpisy dodane później są odkładane na bok, nie giną.',
    restorePointsEmpty:
      'Nie ma jeszcze punktów przywracania. Jeden jest zapisywany przed każdym czyszczeniem lub przywróceniem.',
    restorePointRow: '{label}',
    restorePointCounts: 'Wpisy: {entries}, zdjęcia: {photos}',
    restore: 'Przywróć…',
    restorePointTitle: 'Przywrócić {label}?',
    restorePointLead: 'Wybierz punkt przywracania. Nic nie jest zaznaczone, dopóki nie wybierzesz.',
    restorePointKeptAside: 'Wpisy dodane później (liczba: {count}) są odkładane na bok, nie giną.',
    restorePointSaveFirst: 'Najpierw zapisywany jest dzisiejszy punkt przywracania.',
    restorePointConfirm: 'Przywróć ten punkt',
    restorePointBusy: 'Przywracanie…',
    restorePointNoneChosen: 'Wybierz punkt przywracania, aby kontynuować.',
    versionRestoreTitle: 'Przywrócić v{version}?',
    versionRestoreLead:
      'Przywraca to wygląd i działanie aplikacji z v{version}. Nie zmienia żadnych wpisów ani zdjęć: pozostają bez zmian.',
    versionRestoreDraft:
      'Powstanie nowa wersja robocza. {team} korzysta z v{published}, dopóki nie opublikujesz.',
    versionRestoreConfirm: 'Przywróć v{version} jako wersję roboczą',
    versionRestoreBusy: 'Przywracanie…',
    errorGeneric: 'Nie udało się. Nic nie zostało zmienione. Spróbuj ponownie.',
    resetUnknown: 'Nie udało się potwierdzić, czy czyszczenie się zakończyło.',
    restorePointUnknown: 'Nie udało się potwierdzić, czy przywracanie się zakończyło.',
    restoreVersionUnknown: 'Nie udało się potwierdzić, czy wersja {version} została przywrócona.',
    unknownBlockedHint: 'Możesz spróbować ponownie, gdy będziemy wiedzieć, co się stało.',
    unknownNoCheckHint: 'Odśwież stronę, aby zobaczyć bieżący stan.',
    checkingOutcome: 'Sprawdzanie…',
  },
  pt: {
    close: 'Fechar',
    cancel: 'Cancelar',
    resetTitle: 'Limpar todos os registos de {app}?',
    resetLead:
      'Só os registos e as fotos desta app são limpos. Outras apps, {system} e os dados da empresa não são afetados.',
    resetAudience: '{team} ({people} pessoas) usa esta app. Vão ver uma lista vazia de imediato.',
    resetCleared: 'São limpos {entries} registos e {photos} fotos.',
    resetRestorePoint:
      'Primeiro é guardado um ponto de restauro. Pode restaurá-lo durante {days} dias.',
    resetKeptStays:
      'O histórico guardado mantém-se: as contagens semanais desde {since} não são limpas.',
    resetExternalStays: 'As notas de sinistro já adicionadas em {system} ficam lá.',
    resetTypeLabel: 'Escreva {app} para confirmar',
    resetOwnerOnly:
      'Só o utilizador, como proprietário, pode fazer isto, e apenas aqui. O chat não pode limpar dados reais.',
    resetNotAllowed:
      'Só o proprietário pode limpar registos. {owner} pode fazê-lo a partir deste separador.',
    resetConfirm: 'Limpar {entries} registos',
    resetBusy: 'A limpar…',
    resetDoneTitle: 'Registos limpos',
    resetDoneBody:
      'Foram limpos {entries} registos e {photos} fotos. Pode restaurar o ponto guardado em Dados durante {days} dias.',
    restorePointsHeading: 'Pontos de restauro',
    restorePointsKept: 'Guardados durante {days} dias',
    restorePointsNote:
      'Restaurar repõe os registos e as fotos como estavam. Os registos adicionados entretanto ficam de parte, não se perdem.',
    restorePointsEmpty:
      'Ainda não há pontos de restauro. É guardado um antes de qualquer limpeza ou restauro.',
    restorePointRow: '{label}',
    restorePointCounts: '{entries} registos e {photos} fotos',
    restore: 'Restaurar…',
    restorePointTitle: 'Restaurar {label}?',
    restorePointLead: 'Escolha o ponto de restauro. Nada fica selecionado até escolher.',
    restorePointKeptAside: '{count} registos adicionados entretanto ficam de parte, não se perdem.',
    restorePointSaveFirst: 'Primeiro é guardado um ponto de restauro de hoje.',
    restorePointConfirm: 'Restaurar este ponto',
    restorePointBusy: 'A restaurar…',
    restorePointNoneChosen: 'Escolha um ponto de restauro para continuar.',
    versionRestoreTitle: 'Restaurar v{version}?',
    versionRestoreLead:
      'Isto repõe o aspeto e o funcionamento da app em v{version}. Não altera nenhum registo nem foto: ficam como estão.',
    versionRestoreDraft:
      'Passa a ser um novo rascunho. {team} continua a usar v{published} até publicar.',
    versionRestoreConfirm: 'Restaurar v{version} como rascunho',
    versionRestoreBusy: 'A restaurar…',
    errorGeneric: 'Não funcionou. Nada foi alterado. Tente novamente.',
    resetUnknown: 'Não foi possível confirmar se a limpeza terminou.',
    restorePointUnknown: 'Não foi possível confirmar se o restauro terminou.',
    restoreVersionUnknown: 'Não foi possível confirmar se a versão {version} foi restaurada.',
    unknownBlockedHint: 'Pode tentar novamente quando soubermos o que aconteceu.',
    unknownNoCheckHint: 'Recarregue a página para ver o estado atual.',
    checkingOutcome: 'A verificar…',
  },
  ro: {
    close: 'Închideți',
    cancel: 'Anulați',
    resetTitle: 'Goliți toate înregistrările din {app}?',
    resetLead:
      'Se golesc doar înregistrările și fotografiile acestei aplicații. Celelalte aplicații, {system} și datele companiei rămân neatinse.',
    resetAudience:
      '{team} (persoane: {people}) folosește această aplicație. Vor vedea imediat o listă goală.',
    resetCleared: 'Se golesc înregistrările ({entries}) și fotografiile ({photos}).',
    resetRestorePoint:
      'Mai întâi se salvează un punct de restaurare. Îl puteți restaura în perioada de păstrare (zile: {days}).',
    resetKeptStays: 'Istoricul păstrat rămâne: totalurile săptămânale de la {since} nu se golesc.',
    resetExternalStays: 'Notele privind daunele, deja adăugate în {system}, rămân acolo.',
    resetTypeLabel: 'Tastați {app} pentru a confirma',
    resetOwnerOnly:
      'Doar dumneavoastră, ca proprietar, puteți face acest lucru, și doar aici. Chatul nu poate goli datele live.',
    resetNotAllowed:
      'Doar proprietarul poate goli înregistrările. {owner} poate face acest lucru din această filă.',
    resetConfirm: 'Goliți înregistrările ({entries})',
    resetBusy: 'Se golește…',
    resetDoneTitle: 'Înregistrări golite',
    resetDoneBody:
      'Au fost golite înregistrările ({entries}) și fotografiile ({photos}). Restaurați punctul salvat din Date în perioada de păstrare (zile: {days}).',
    restorePointsHeading: 'Puncte de restaurare',
    restorePointsKept: 'Zile de păstrare: {days}',
    restorePointsNote:
      'Restaurarea readuce înregistrările și fotografiile așa cum erau. Înregistrările adăugate între timp sunt puse deoparte, nu pierdute.',
    restorePointsEmpty:
      'Încă nu există puncte de restaurare. Se salvează unul înainte ca ceva să fie golit sau restaurat.',
    restorePointRow: '{label}',
    restorePointCounts: 'Înregistrări: {entries}, fotografii: {photos}',
    restore: 'Restaurați…',
    restorePointTitle: 'Restaurați {label}?',
    restorePointLead: 'Alegeți punctul de restaurare. Nimic nu este selectat până nu alegeți.',
    restorePointKeptAside:
      'Înregistrările adăugate între timp ({count}) sunt puse deoparte, nu pierdute.',
    restorePointSaveFirst: 'Mai întâi se salvează un punct de restaurare de azi.',
    restorePointConfirm: 'Restaurați acest punct',
    restorePointBusy: 'Se restaurează…',
    restorePointNoneChosen: 'Alegeți un punct de restaurare pentru a continua.',
    versionRestoreTitle: 'Restaurați v{version}?',
    versionRestoreLead:
      'Aplicația va arăta și va funcționa din nou ca în v{version}. Nu se modifică nicio înregistrare sau fotografie: rămân așa cum sunt.',
    versionRestoreDraft:
      'Devine o ciornă nouă. {team} folosește în continuare v{published} până când publicați.',
    versionRestoreConfirm: 'Restaurați v{version} ca ciornă',
    versionRestoreBusy: 'Se restaurează…',
    errorGeneric: 'Nu a funcționat. Nu s-a modificat nimic. Încercați din nou.',
    resetUnknown: 'Nu am putut confirma dacă golirea s-a încheiat.',
    restorePointUnknown: 'Nu am putut confirma dacă restaurarea s-a încheiat.',
    restoreVersionUnknown: 'Nu am putut confirma dacă versiunea {version} a fost restaurată.',
    unknownBlockedHint: 'Puteți încerca din nou după ce aflăm ce s-a întâmplat.',
    unknownNoCheckHint: 'Reîncărcați pagina pentru a vedea starea actuală.',
    checkingOutcome: 'Se verifică…',
  },
  sk: {
    close: 'Zavrieť',
    cancel: 'Zrušiť',
    resetTitle: 'Vymazať všetky záznamy v {app}?',
    resetLead:
      'Vymažú sa len záznamy a fotky tejto aplikácie. Ostatných aplikácií, {system} ani firemných údajov sa to nedotkne.',
    resetAudience:
      'Túto aplikáciu používa {team} (počet ľudí: {people}). Prázdny zoznam uvidia hneď.',
    resetCleared: 'Vymaže sa – záznamy: {entries}, fotky: {photos}.',
    resetRestorePoint:
      'Najprv sa uloží bod obnovenia. Obnoviť ho môžete počas obmedzenej doby (počet dní: {days}).',
    resetKeptStays: 'Uchovaná história zostáva: týždenné súhrny od {since} sa nevymažú.',
    resetExternalStays: 'Poznámky k nárokom, ktoré už sú v {system}, tam zostanú.',
    resetTypeLabel: 'Na potvrdenie napíšte {app}',
    resetOwnerOnly:
      'Urobiť to môžete len vy ako vlastník, a len tu. Chat nemôže vymazať živé údaje.',
    resetNotAllowed: 'Záznamy môže vymazať len vlastník. {owner} to môže urobiť na tejto karte.',
    resetConfirm: 'Vymazať záznamy: {entries}',
    resetBusy: 'Maže sa…',
    resetDoneTitle: 'Záznamy sú vymazané',
    resetDoneBody:
      'Vymazané – záznamy: {entries}, fotky: {photos}. Uložený bod môžete obnoviť v časti Údaje počas obmedzenej doby (počet dní: {days}).',
    restorePointsHeading: 'Body obnovenia',
    restorePointsKept: 'Uchovanie (počet dní): {days}',
    restorePointsNote:
      'Obnovenie vráti záznamy a fotky do pôvodného stavu. Záznamy pridané medzitým sa odložia nabok, nestratia sa.',
    restorePointsEmpty:
      'Zatiaľ žiadne body obnovenia. Jeden sa uloží pred každým vymazaním alebo obnovením.',
    restorePointRow: '{label}',
    restorePointCounts: 'Záznamy: {entries}, fotky: {photos}',
    restore: 'Obnoviť…',
    restorePointTitle: 'Obnoviť {label}?',
    restorePointLead: 'Vyberte bod obnovenia. Kým nevyberiete, nič nie je vybrané.',
    restorePointKeptAside:
      'Záznamy pridané medzitým (počet: {count}) sa odložia nabok, nestratia sa.',
    restorePointSaveFirst: 'Najprv sa uloží dnešný bod obnovenia.',
    restorePointConfirm: 'Obnoviť tento bod',
    restorePointBusy: 'Obnovuje sa…',
    restorePointNoneChosen: 'Ak chcete pokračovať, vyberte bod obnovenia.',
    versionRestoreTitle: 'Obnoviť v{version}?',
    versionRestoreLead:
      'Vráti sa vzhľad a fungovanie aplikácie z v{version}. Nezmenia sa žiadne záznamy ani fotky: zostanú, ako sú.',
    versionRestoreDraft:
      'Vznikne nový koncept. {team} bude ďalej používať v{published}, kým ho nepublikujete.',
    versionRestoreConfirm: 'Obnoviť v{version} ako koncept',
    versionRestoreBusy: 'Obnovuje sa…',
    errorGeneric: 'Nepodarilo sa. Nič sa nezmenilo. Skúste to znova.',
    resetUnknown: 'Nepodarilo sa overiť, či sa vymazanie dokončilo.',
    restorePointUnknown: 'Nepodarilo sa overiť, či sa obnovenie dokončilo.',
    restoreVersionUnknown: 'Nepodarilo sa overiť, či bola verzia {version} obnovená.',
    unknownBlockedHint: 'Skúsiť to znova môžete, keď budeme vedieť, čo sa stalo.',
    unknownNoCheckHint: 'Načítajte stránku znova, aby ste videli aktuálny stav.',
    checkingOutcome: 'Kontroluje sa…',
  },
  sl: {
    close: 'Zapri',
    cancel: 'Prekliči',
    resetTitle: 'Izbrisati vse vnose v {app}?',
    resetLead:
      'Izbrisani bodo samo vnosi in fotografije te aplikacije. Druge aplikacije, {system} in podatki podjetja ostanejo nedotaknjeni.',
    resetAudience:
      'To aplikacijo uporablja {team} (število oseb: {people}). Takoj bodo videli prazen seznam.',
    resetCleared: 'Izbrisano bo – vnosi: {entries}, fotografije: {photos}.',
    resetRestorePoint:
      'Najprej se shrani točka obnovitve. Obnovite jo lahko v omejenem času (število dni: {days}).',
    resetKeptStays: 'Shranjena zgodovina ostane: tedenski seštevki od {since} se ne izbrišejo.',
    resetExternalStays: 'Opombe k zahtevkom, ki so že dodane v {system}, tam ostanejo.',
    resetTypeLabel: 'Za potrditev vnesite {app}',
    resetOwnerOnly:
      'To lahko storite samo vi kot lastnik, in samo tukaj. Klepet ne more brisati podatkov v živo.',
    resetNotAllowed: 'Vnose lahko izbriše samo lastnik. {owner} lahko to stori na tem zavihku.',
    resetConfirm: 'Izbriši vnose: {entries}',
    resetBusy: 'Brisanje…',
    resetDoneTitle: 'Vnosi so izbrisani',
    resetDoneBody:
      'Izbrisano – vnosi: {entries}, fotografije: {photos}. Shranjeno točko lahko obnovite v razdelku Podatki v omejenem času (število dni: {days}).',
    restorePointsHeading: 'Točke obnovitve',
    restorePointsKept: 'Hramba (število dni): {days}',
    restorePointsNote:
      'Obnovitev povrne vnose in fotografije v prejšnje stanje. Vnosi, dodani pozneje, se odložijo na stran, ne izgubijo se.',
    restorePointsEmpty:
      'Točk obnovitve še ni. Ena se shrani pred vsakim brisanjem ali obnovitvijo.',
    restorePointRow: '{label}',
    restorePointCounts: 'Vnosi: {entries}, fotografije: {photos}',
    restore: 'Obnovi…',
    restorePointTitle: 'Obnoviti {label}?',
    restorePointLead: 'Izberite točko obnovitve. Dokler ne izberete, ni nič izbrano.',
    restorePointKeptAside:
      'Vnosi, dodani pozneje (število: {count}), se odložijo na stran, ne izgubijo se.',
    restorePointSaveFirst: 'Najprej se shrani današnja točka obnovitve.',
    restorePointConfirm: 'Obnovi to točko',
    restorePointBusy: 'Obnavljanje…',
    restorePointNoneChosen: 'Za nadaljevanje izberite točko obnovitve.',
    versionRestoreTitle: 'Obnoviti v{version}?',
    versionRestoreLead:
      'S tem se povrneta videz in delovanje aplikacije iz v{version}. Vnosi in fotografije se ne spremenijo: ostanejo, kot so.',
    versionRestoreDraft:
      'Nastane nov osnutek. {team} še naprej uporablja v{published}, dokler ne objavite.',
    versionRestoreConfirm: 'Obnovi v{version} kot osnutek',
    versionRestoreBusy: 'Obnavljanje…',
    errorGeneric: 'Ni uspelo. Nič ni bilo spremenjeno. Poskusite znova.',
    resetUnknown: 'Nismo mogli potrditi, ali se je brisanje končalo.',
    restorePointUnknown: 'Nismo mogli potrditi, ali se je obnovitev končala.',
    restoreVersionUnknown: 'Nismo mogli potrditi, ali je bila različica {version} obnovljena.',
    unknownBlockedHint: 'Znova lahko poskusite, ko bomo vedeli, kaj se je zgodilo.',
    unknownNoCheckHint: 'Znova naložite stran, da vidite trenutno stanje.',
    checkingOutcome: 'Preverjanje…',
  },
};

export const appLifecycleCopy: NamespaceCatalog<AppLifecycleCopyKey> = {
  en,
  bg: translated.bg,
  cs: translated.cs,
  da: translated.da,
  de: translated.de,
  el: translated.el,
  es: translated.es,
  et: translated.et,
  fi: translated.fi,
  fr: translated.fr,
  ga: translated.ga,
  hr: translated.hr,
  hu: translated.hu,
  it: translated.it,
  lt: translated.lt,
  lv: translated.lv,
  mt: translated.mt,
  nl: translated.nl,
  pl: translated.pl,
  pt: translated.pt,
  ro: translated.ro,
  sk: translated.sk,
  sl: translated.sl,
  sv,
};
export default appLifecycleCopy;
